import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LiveRow } from "@/components/live/LiveRow";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { PieChart as PieIcon, Pause, Play, RefreshCw } from "lucide-react";

const REFRESH_MS = 10000;

export default function LivePortfolios() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const failed = useRef(false);

  const load = useCallback(async (silent = true) => {
    try {
      const { data: d } = await api.get("/invest/live");
      setData(d);
      failed.current = false;
    } catch (e) {
      if (!silent || !failed.current) toast.error("Could not refresh your live portfolios");
      failed.current = true;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    if (!live) return undefined;
    const iv = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(iv);
  }, [live, load]);

  const t = data?.totals;
  const stamp = data?.as_of ? new Date(data.as_of).toLocaleTimeString() : "—";

  return (
    <AppShell>
      <PageHeader title="Live portfolios" subtitle="Every managed portfolio and holding, refreshing on its own"
        testid="live-header"
        action={
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground numeric hidden sm:inline" data-testid="live-timestamp">
              {stamp}
            </span>
            <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => load(false)} data-testid="live-refresh">
              <RefreshCw className="h-3.5 w-3.5" />Refresh
            </Button>
            <Button variant={live ? "default" : "outline"} size="sm" className="gap-1.5"
              onClick={() => setLive((v) => !v)} data-testid="live-toggle">
              {live ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              {live ? "Live" : "Paused"}
            </Button>
          </div>
        } />

      <div className="grid gap-4 sm:gap-5 grid-cols-2 lg:grid-cols-4 mb-4 sm:mb-5">
        <Kpi label="Portfolio value" value={fmtUSD(t?.value_usd || 0)} testid="live-total-value" accent />
        <Kpi label="Today" value={fmtUSD(t?.day_change_usd || 0)} change={t?.day_change_pct} testid="live-total-day" />
        <Kpi label="Total P&L" value={fmtUSD(t?.pnl_usd || 0)} change={t?.pnl_pct} testid="live-total-pnl" />
        <Kpi label="Portfolios" value={t?.portfolios ?? 0} testid="live-total-count" />
      </div>

      <Card className="shadow-premium overflow-hidden" data-testid="live-table-card">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="p-6">
            <EmptyState title="No portfolios yet"
              hint="Build one on the Invest page and it will show up here, live." testid="live-empty" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="live-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[220px]">Portfolio</TableHead>
                  <TableHead className="text-end">Value</TableHead>
                  <TableHead className="text-end">Today</TableHead>
                  <TableHead className="text-end">P&amp;L</TableHead>
                  <TableHead className="text-end">Drift</TableHead>
                  <TableHead className="text-end">Auto-invest</TableHead>
                  <TableHead className="text-end min-w-[190px]">Rebalance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => <LiveRow key={r.portfolio_id} row={r} onRefresh={() => load(false)} />)}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <p className="text-[11px] text-muted-foreground mt-3">
        Prices refresh every {REFRESH_MS / 1000} seconds. Drift is the largest gap between a holding's actual
        weight and its target — switch on auto-rebalance and we'll close it for you and email what changed.
      </p>
    </AppShell>
  );
}
